/**
 * Navigation Utility for PWA and Native Apps
 * 
 * Handles navigation in a way that works across:
 * - Regular web browser (opens new tab for external links)
 * - PWA (opens in same window)
 * - Native iOS/Android app (opens in same window or in-app browser)
 */

import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

// Check if we're in a native app or PWA
const isNativeApp = Capacitor.isNativePlatform();
const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
              window.navigator.standalone === true;

/**
 * Navigate to an internal route (within the app)
 * Always opens in the same window/view
 */
export const navigateToRoute = (path, navigate) => {
  if (navigate) {
    navigate(path);
  } else {
    window.location.href = path;
  }
};

/**
 * Open an internal app URL (like a ticket page)
 * For PWA/native: opens in same window
 * For web: can optionally open in new tab
 */
export const openInternalUrl = (url, options = {}) => {
  const { forceNewTab = false, navigate } = options;
  
  // For native app or PWA, always open in same window
  if (isNativeApp || isPWA) {
    window.location.href = url;
    return;
  }
  
  // For regular web, respect the forceNewTab option
  if (forceNewTab) {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    window.location.href = url;
  }
};

/**
 * Open an external URL (like Stripe dashboard, external websites)
 * For native: uses Capacitor Browser plugin (in-app browser)
 * For PWA/web: opens in new tab
 */
export const openExternalUrl = async (url) => {
  if (isNativeApp) {
    try {
      // Use Capacitor's Browser plugin for in-app browser experience
      await Browser.open({ url });
    } catch (error) {
      // Fallback to regular open
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  } else {
    // For web/PWA, open in new tab
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

/**
 * Open a ticket/booking detail page
 * This is commonly used in booking views
 */
export const openTicketPage = (ticketUrl) => {
  // Tickets should open in same view for app/PWA
  openInternalUrl(ticketUrl);
};

/**
 * Open QR scanner URL
 */
export const openQRScanner = (scannerUrl) => {
  // Scanner should open in same view
  openInternalUrl(scannerUrl);
};

/**
 * Open checkout URL (Stripe, etc.)
 * For payments, we might want to keep user in the app if possible
 */
export const openCheckoutUrl = async (checkoutUrl) => {
  if (isNativeApp) {
    // For native apps, use in-app browser for payment flows
    try {
      await Browser.open({ url: checkoutUrl });
    } catch (error) {
      window.location.href = checkoutUrl;
    }
  } else {
    // For web, open in same window to handle redirect back properly
    window.location.href = checkoutUrl;
  }
};

/**
 * Check if we're in app/PWA mode
 */
export const isAppMode = () => isNativeApp || isPWA;

/**
 * Get platform info for debugging
 */
export const getPlatformInfo = () => ({
  isNativeApp,
  isPWA,
  platform: Capacitor.getPlatform(),
  isAppMode: isNativeApp || isPWA
});

export default {
  navigateToRoute,
  openInternalUrl,
  openExternalUrl,
  openTicketPage,
  openQRScanner,
  openCheckoutUrl,
  isAppMode,
  getPlatformInfo
};
